import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import {
  EventScope,
  useClickEvent,
  useInViewedEvent,
} from '@autopass-web/mochi-analytics/event';
import {
  DialogLayout,
  MochiButton,
  MochiCheckbox,
  MochiInput,
} from '@autopass-web/mochi-component';
import { CoreIcon } from '@autopass-web/mochi-icons';

import { environment } from '../environments/environment';

const emailSchema = z
  .string()
  .trim()
  .min(1, { message: '請輸入有效的電子郵件地址' })
  .email({ message: '請輸入有效的電子郵件地址' });

interface DpassSignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void> | void;
  isSubmitting?: boolean;
  onOpen?: () => void;
}

const DPASS_SIGNUP_DIALOG_SCOPE = '加入駕駛中心提醒視窗';

const DpassSignupDialogContent = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  onOpen,
}: DpassSignupDialogProps) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const logInView = useInViewedEvent('提醒視窗');
  const logContinueClick = useClickEvent('繼續付款');
  const logCloseClick = useClickEvent('關閉視窗');
  const logCloseIconClick = useClickEvent('關閉視窗icon');

  useEffect(() => {
    if (isOpen) {
      logInView({}, { key: 'dpass-signup-dialog' });
      onOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleConfirm = async () => {
    const parseResult = emailSchema.safeParse(email);

    if (!parseResult.success) {
      setErrorMessage(
        (parseResult as z.SafeParseError<string>).error.issues[0]?.message ??
          '請輸入有效的電子郵件地址',
      );
      return;
    }

    setErrorMessage(null);
    logContinueClick({ email });
    await onSubmit(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <DialogLayout show={isOpen}>
      <div className="mx-6 mt-10 mb-2">
        <CoreIcon
          src="/assets/close.svg"
          onClick={() => {
            onClose();
            logCloseIconClick();
          }}
          className="absolute top-3 right-[15px]"
        />
        <h1 className="text-xl font-bold text-center mb-4">
          完成資訊以繼續付款
        </h1>
        <p className="text-xs text-gray-500 mb-4">
          您已勾選加入駕駛中心，請填寫電子信箱以接收帳號驗證連結，點擊下方「繼續付款」按鈕，以完成本次交易。
        </p>
        <MochiInput
          variant="grey"
          inputMode="email"
          autoComplete="email"
          placeholder="輸入 Email 信箱"
          value={email}
          errorMessage={errorMessage}
          onChange={handleEmailChange}
        />
        <div className="flex flex-col gap-2 mt-6">
          <MochiButton
            type="button"
            variant="solid-round"
            size="full"
            disabled={!email || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleConfirm}
            className="bg-[#171E2B] rounded-[10px]"
          >
            繼續付款
          </MochiButton>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              onClose();
              logCloseClick();
            }}
            className="text-[#073A7E] font-bold my-2"
          >
            下次再加入
          </button>
        </div>
      </div>
    </DialogLayout>
  );
};

const DpassSignupDialog = (props: DpassSignupDialogProps) => (
  <EventScope eventProps={{ scope: DPASS_SIGNUP_DIALOG_SCOPE }}>
    <DpassSignupDialogContent {...props} />
  </EventScope>
);

const DpassSignupCheckbox = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className="flex items-center gap-2 pt-2 pb-3 px-5">
      <MochiCheckbox
        value="dpass-signup"
        checked={checked}
        onChange={(e) => {
          const checked = e.target.checked;
          onChange(checked);
        }}
      />
      <span className="text-xs">
        點擊左方按鈕同意加入
        <a
          className="underline text-black"
          href={environment.dpassAppUrl}
          target="_blank"
          rel="noreferrer"
        >
          駕駛中心
        </a>
        並享有支付優惠服務且同意
        <a
          className="underline text-black"
          href={environment.termsOfServiceUrl}
          target="_blank"
          rel="noreferrer"
        >
          服務條款
        </a>
        與
        <a
          className="underline text-black"
          href={environment.privacyPolicyUrl}
          target="_blank"
          rel="noreferrer"
        >
          隱私權政策
        </a>
        。取消勾選後仍可完成本次交易。
      </span>
    </div>
  );
};

const useDpassEmailPrompt = ({
  onDismiss,
}: { onDismiss?: () => void } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailResolveRef = useRef<(value: string | null) => void>();
  const doneResolveRef = useRef<() => void>();
  const donePromiseRef = useRef<Promise<void>>();

  const promptEmail = useCallback((): Promise<string | null> => {
    donePromiseRef.current = new Promise<void>((resolve) => {
      doneResolveRef.current = resolve;
    });

    setIsOpen(true);
    return new Promise<string | null>((resolve) => {
      emailResolveRef.current = resolve;
    });
  }, []);

  const completePrompt = useCallback(() => {
    doneResolveRef.current?.();
  }, []);

  const handleSubmit = useCallback(async (email: string) => {
    setIsSubmitting(true);
    emailResolveRef.current?.(email);
    await donePromiseRef.current;
    setIsSubmitting(false);
    setIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onDismiss?.();
    emailResolveRef.current?.(null);
  }, [onDismiss]);

  return {
    isOpen,
    isSubmitting,
    promptEmail,
    completePrompt,
    handleSubmit,
    handleClose,
  };
};

export { DpassSignupCheckbox, DpassSignupDialog, useDpassEmailPrompt };
